<template>
  <Context ref="context" class="view-identity" overflow-scroll>
    <!-- The preview confirms the combination before it is committed. Icon and
         colour are independent choices, so seeing them together is the only
         way to judge either. -->
    <div class="view-identity__preview">
      <i
        class="view-identity__preview-icon"
        :class="[previewColorClass, previewIconClass]"
        aria-hidden="true"
      ></i>
      <span class="view-identity__preview-name">{{ view.name }}</span>
    </div>

    <div class="view-identity__section">
      <div :id="iconLabelId" class="view-identity__section-label">
        {{ $t('viewIdentity.icon') }}
      </div>

      <!-- The catalogue is browsable by group, but "which icon means done?" is
           faster to type than to hunt for once there are two hundred of them,
           so search is the primary path and the groups are the fallback. -->
      <div class="view-identity__search">
        <i
          class="view-identity__search-icon iconoir-search"
          aria-hidden="true"
        ></i>
        <input
          v-model="iconQuery"
          type="text"
          class="view-identity__search-input"
          :placeholder="$t('viewIdentity.searchIcons')"
          :aria-label="$t('viewIdentity.searchIcons')"
        />
      </div>

      <div
        class="view-identity__icons-scroll"
        role="group"
        :aria-labelledby="iconLabelId"
      >
        <!-- The reset tile carries the view type's own icon, so the user is
             always shown what the default is in the same interaction that
             overrides it. It belongs to the picker rather than to any group, so
             it sits above them and is hidden while searching, where a tile that
             matches no query would just be noise. -->
        <button
          v-if="!iconQuery"
          type="button"
          class="view-identity__icon view-identity__icon--reset"
          :class="{ 'view-identity__icon--active': !view.icon }"
          :aria-pressed="!view.icon ? 'true' : 'false'"
          :title="$t('viewIdentity.useTypeIcon')"
          :aria-label="$t('viewIdentity.useTypeIcon')"
          @click="selectIcon('')"
        >
          <i :class="typeIconClass" aria-hidden="true"></i>
        </button>

        <div
          v-for="group in visibleIconGroups"
          :key="group.key"
          class="view-identity__icon-group"
        >
          <div class="view-identity__group-label">
            {{ $t(`viewIdentity.iconGroup.${group.key}`) }}
          </div>
          <div class="view-identity__icons">
            <button
              v-for="icon in group.icons"
              :key="icon"
              type="button"
              class="view-identity__icon"
              :class="{ 'view-identity__icon--active': view.icon === icon }"
              :aria-pressed="view.icon === icon ? 'true' : 'false'"
              :title="icon"
              :aria-label="icon"
              @click="selectIcon(icon)"
            >
              <i :class="`iconoir-${icon}`" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div v-if="!visibleIconGroups.length" class="view-identity__empty">
          {{ $t('viewIdentity.noIcons', { query: iconQuery }) }}
        </div>
      </div>
    </div>

    <div class="view-identity__section">
      <div :id="colorLabelId" class="view-identity__section-label">
        {{ $t('viewIdentity.color') }}
      </div>
      <div
        class="view-identity__colors"
        role="group"
        :aria-labelledby="colorLabelId"
      >
        <button
          type="button"
          class="view-identity__color view-identity__color--none"
          :class="{ 'view-identity__color--active': !view.color }"
          :aria-pressed="!view.color ? 'true' : 'false'"
          :title="$t('viewIdentity.noColor')"
          :aria-label="$t('viewIdentity.noColor')"
          @click="selectColor('')"
        >
          <i class="iconoir-prohibition" aria-hidden="true"></i>
        </button>
        <button
          v-for="color in colors"
          :key="color"
          type="button"
          class="view-identity__color"
          :class="[
            `view-color--${color}`,
            { 'view-identity__color--active': view.color === color },
          ]"
          :title="color"
          :aria-label="color"
          :aria-pressed="view.color === color ? 'true' : 'false'"
          @click="selectColor(color)"
        ></button>
      </div>
    </div>

    <div class="view-identity__foot">
      <a
        class="view-identity__reset"
        :class="{ 'view-identity__reset--disabled': !hasIdentity }"
        @click="reset"
        >{{ $t('viewIdentity.reset') }}</a
      >
    </div>
  </Context>
</template>

<script>
import context from '@baserow/modules/core/mixins/context'
import { notifyIf } from '@baserow/modules/core/utils/error'
import { VIEW_ICON_GROUPS } from '@baserow/modules/database/viewIcons'
import { VIEW_COLORS } from '@baserow/modules/database/viewColors'
import {
  getViewIconClass,
  getViewColorClass,
} from '@baserow/modules/database/utils/viewIdentity'

/**
 * The per-view icon and colour picker.
 *
 * The icon list is curated rather than free text, but it is large enough
 * (`VIEW_ICONS`, ~200 across twelve groups) that browsing alone is not a fair
 * way to find one, so it is presented as labelled groups behind a search field:
 * type if you know the word, scan the groups if you do not.
 *
 * The colour list is a subset of `BASEROW_COLORS` -- one swatch per hue -- and
 * not a slice of the frontend `$colors` map, which is a superset and would
 * offer values the API rejects with a 400. See `viewColors.js`.
 *
 * Every click writes immediately through `view/update`, which is optimistic and
 * rolls back on failure, so there is no save button. That matches how colour is
 * picked everywhere else in this product.
 */
export default {
  name: 'ViewIdentityContext',
  mixins: [context],
  props: {
    view: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      colors: VIEW_COLORS,
      iconQuery: '',
    }
  },
  computed: {
    /**
     * The catalogue filtered by the search box, with empty groups dropped so
     * that a query never leaves a labelled section standing with nothing under
     * it.
     *
     * Matching is a substring test on the icon's own name, which is what the
     * tile's tooltip and aria-label already say -- so the thing a user searches
     * for is the thing they were shown. Group keys are deliberately NOT matched:
     * typing "files" would otherwise return the whole Files group regardless of
     * what those icons are called, which reads as the search having ignored the
     * query.
     */
    visibleIconGroups() {
      const query = this.iconQuery.trim().toLowerCase()
      if (!query) {
        return VIEW_ICON_GROUPS
      }
      return VIEW_ICON_GROUPS.map((group) => ({
        ...group,
        icons: group.icons.filter((icon) => icon.includes(query)),
      })).filter((group) => group.icons.length > 0)
    },
    // The two groups are labelled for screen readers. One picker exists per
    // view row, so the view id is enough to keep the ids unique in the page.
    iconLabelId() {
      return `view-identity-icon-${this.view.id}`
    },
    colorLabelId() {
      return `view-identity-color-${this.view.id}`
    },
    typeIconClass() {
      return this.view._.type.iconClass
    },
    previewIconClass() {
      return getViewIconClass(this.view)
    },
    previewColorClass() {
      return getViewColorClass(this.view)
    },
    hasIdentity() {
      return Boolean(this.view.icon || this.view.color)
    },
  },
  methods: {
    selectIcon(icon) {
      // Clicking the already-active tile is a no-op rather than a toggle: the
      // reset tile is the documented way back to the default, so a second click
      // never silently clears a deliberate choice.
      if ((this.view.icon || '') === icon) {
        return
      }
      this.update({ icon })
    },
    selectColor(color) {
      if ((this.view.color || '') === color) {
        return
      }
      this.update({ color })
    },
    reset() {
      if (!this.hasIdentity) {
        return
      }
      this.update({ icon: '', color: '' })
    },
    async update(values) {
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values,
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
  },
}
</script>
